/**
 * RemindersGuideModal
 *
 * Guia explicativo dos lembretes automáticos de agendamento.
 * Aberto por botão "Guia" no header do RemindersModal.
 */

import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconBell,
  IconClock,
  IconBrandWhatsapp,
  IconMail,
  IconBulb,
  IconAlertTriangle,
} from '@tabler/icons-react';
import '../../styles/RemindersModal.css';

const RemindersGuideModal = ({ show, onHide }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="rem-modal"
      scrollable
    >
      <Modal.Header closeButton className="rem-modal__header">
        <Modal.Title className="rem-modal__title">
          <div className="rem-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia — Lembretes automáticos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="rem-modal__body">
        <p className="rem-modal__intro">
          Lembretes são mensagens enviadas <strong>automaticamente</strong> via WhatsApp ou
          e-mail. Servem para reduzir faltas, confirmar presença, enviar dicas pré-consulta
          e coletar feedback pós-atendimento. Cada lembrete pode ser disparado
          {' '}<strong>antes do evento</strong>, <strong>após a criação do agendamento</strong>{' '}
          ou <strong>após o atendimento</strong>.
        </p>

        <section className="rem-section">
          <h4 className="rem-section__title">
            <IconBell size={16} /> Como funciona
          </h4>
          <ol className="rem-list">
            <li>O cliente agenda pelo agente (ex.: "quero corte sábado às 14h").</li>
            <li>Para cada lembrete configurado aqui, o sistema agenda uma mensagem futura.
              Modos:
              <ul style={{ marginTop: 4 }}>
                <li><strong>Antes do evento</strong>: ex. 24h antes do horário marcado, 2h antes — referência é o horário do agendamento.</li>
                <li><strong>Após criação</strong>: ex. 1h após o cliente agendar, com dicas pré-consulta — referência é o momento que o agendamento foi criado.</li>
                <li><strong>Após o atendimento</strong>: ex. 2h após o término do atendimento, para pesquisa de satisfação (NPS), pedido de avaliação ou follow-up — referência é o horário de término do evento.</li>
              </ul>
            </li>
            <li>Na hora certa, a mensagem é despachada no canal escolhido com os dados reais
              do agendamento.</li>
            <li>Se o agendamento for <strong>cancelado</strong> ou marcado como{' '}
              <strong>faltou (missing)</strong>, todos os lembretes pendentes
              (antes do evento, após criação e após o atendimento) são cancelados
              automaticamente. Se for <strong>remarcado</strong>, os lembretes <em>antes do evento</em>{' '}
              são recalculados para o novo horário e os de <em>após o atendimento</em> para o novo
              horário de término — os de <em>após criação</em> mantêm a data original
              (a âncora não muda).</li>
            <li>Lembretes <em>após criação</em> cujo offset cairia depois do horário do evento
              são descartados automaticamente (provável erro de config).</li>
          </ol>
        </section>

        <section className="rem-section">
          <h4 className="rem-section__title">
            <IconClock size={16} /> Variáveis disponíveis na mensagem
          </h4>
          <table className="rem-vars-table">
            <thead>
              <tr><th>Variável</th><th>Significado</th><th>Exemplo</th></tr>
            </thead>
            <tbody>
              <tr><td><code>{'{{nome}}'}</code></td><td>Nome do cliente</td><td>Sérgio</td></tr>
              <tr><td><code>{'{{data}}'}</code></td><td>Data do agendamento</td><td>25/05/2026</td></tr>
              <tr><td><code>{'{{horario}}'}</code></td><td>Horário</td><td>14:00</td></tr>
              <tr><td><code>{'{{servico}}'}</code></td><td>Tipo de atendimento</td><td>Corte a Seco</td></tr>
              <tr><td><code>{'{{profissional}}'}</code></td><td>Nome do calendário/profissional</td><td>Paulo</td></tr>
            </tbody>
          </table>
        </section>

        <section className="rem-section">
          <h4 className="rem-section__title">
            <IconBulb size={16} /> Exemplos prontos
          </h4>
          <div className="rem-example">
            <div className="rem-example__head">
              <IconBrandWhatsapp size={14} style={{ color: '#10b981' }} /> WhatsApp · 24h antes
            </div>
            <div className="rem-example__body">
              Olá {'{{nome}}'}! 👋 Passando pra lembrar do seu {'{{servico}}'} amanhã ({'{{data}}'}) às {'{{horario}}'} com {'{{profissional}}'}. Te espero!
            </div>
          </div>
          <div className="rem-example">
            <div className="rem-example__head">
              <IconBrandWhatsapp size={14} style={{ color: '#10b981' }} /> WhatsApp · 2h antes
            </div>
            <div className="rem-example__body">
              {'{{nome}}'}, seu {'{{servico}}'} é hoje às {'{{horario}}'}! Confirma sua presença, por favor? 🙂
            </div>
          </div>
          <div className="rem-example">
            <div className="rem-example__head">
              <IconMail size={14} style={{ color: '#3b82f6' }} /> E-mail · 1 dia antes
            </div>
            <div className="rem-example__body">
              <strong>Assunto:</strong> Lembrete: {'{{servico}}'} amanhã às {'{{horario}}'}<br />
              <strong>Corpo:</strong> Olá {'{{nome}}'}, confirmamos seu {'{{servico}}'} para {'{{data}}'} às {'{{horario}}'} com {'{{profissional}}'}. Em caso de imprevisto, responda este e-mail.
            </div>
          </div>
          <div className="rem-example">
            <div className="rem-example__head">
              <IconBrandWhatsapp size={14} style={{ color: '#10b981' }} /> WhatsApp · 1h após criação
            </div>
            <div className="rem-example__body">
              Oi {'{{nome}}'}! Já marcamos seu {'{{servico}}'} para {'{{data}}'} às {'{{horario}}'}. Dicas pra aproveitar melhor: chegue 5 min antes e venha com o cabelo seco. Qualquer dúvida, é só chamar! 💜
            </div>
          </div>
          <div className="rem-example">
            <div className="rem-example__head">
              <IconBrandWhatsapp size={14} style={{ color: '#10b981' }} /> WhatsApp · 2h após o atendimento (pesquisa NPS)
            </div>
            <div className="rem-example__body">
              Oi {'{{nome}}'}! Esperamos que tenha curtido seu {'{{servico}}'} com {'{{profissional}}'}.
              De 0 a 10, quanto você indicaria a gente para um amigo? Sua resposta ajuda demais! 🙏
            </div>
          </div>
        </section>

        <div className="rem-callout rem-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Janela de 24h para WhatsApp Cloud API e Instagram DM</strong> (aplicável quando
            o tenant operar via Cloud API ou Instagram — em rollout). A Meta só entrega mensagens
            iniciadas pela empresa se o cliente trocou alguma mensagem nas últimas 24h. Lembretes
            <em> após o atendimento</em> costumam cair fora dessa janela e podem não ser entregues
            — para garantir entrega fora da janela, é preciso usar templates HSM aprovados (não
            cobertos nessa versão). No WhatsApp não oficial esse limite não se aplica.
          </div>
        </div>

        <div className="rem-callout rem-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Marque eventos como "Faltou"</strong> quando o cliente não comparecer.
            Sem isso, lembretes <em>após o atendimento</em> (ex.: pesquisa de satisfação) vão
            ser disparados normalmente para quem não veio — efeito ruim. O status <em>faltou</em>
            cancela automaticamente os lembretes pós-atendimento pendentes.
          </div>
        </div>

        <div className="rem-callout rem-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>E-mails só enviam se o cliente tiver e-mail cadastrado</strong> (campo coletado
            pelo agente durante a conversa). Se não houver e-mail, o lembrete fica como
            <em> falha de destinatário</em> mas não interrompe outros lembretes.
          </div>
        </div>

        <div className="rem-callout rem-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Antecipações típicas:</strong> 24h, 2h e 30min cobrem a maioria dos negócios.
            Para serviços longos ou caros, vale 48h também. Para pesquisa pós-atendimento,
            2h depois do término é o offset mais comum.
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="rem-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Entendi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemindersGuideModal;
